import { Link } from "react-router-dom";
import { useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { ShieldMark } from "./ShieldMark";
import { ThemeToggle } from "./ThemeToggle";
import { getTools } from "../../data/tools";
import { useLang } from "../../i18n/LanguageContext";

export const MobileNavigation = ({ open, onClose }) => {
  const reduce = useReducedMotion();
  const { lang, t, setLang } = useLang();
  const tools = getTools(lang);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  const links = [
    { label: t.nav.ecosistema, to: "/ecosistema" },
    { label: t.nav.soluciones, to: "/soluciones" },
    { label: t.nav.seguridad, to: "/seguridad-y-gobierno" },
    { label: t.nav.empresa, to: "/empresa" },
    { label: t.nav.contacto, to: "/contacto" },
  ];
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="mobile-navigation"
          className="fixed inset-0 z-[80] bg-onix-black overflow-y-auto"
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          role="dialog"
          aria-modal="true"
          aria-label={t.header.mainNav}
        >
          <div className="onix-shell flex items-center justify-between h-[76px] border-b border-onix-line">
            <div className="flex items-center gap-4">
              <ShieldMark variant="white" className="w-[52px]" eager alt="Escudo ONIX" />
              <span className="font-sans font-bold text-base tracking-[0.3em] text-onix-white">ONIX</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-[11px] font-bold tracking-[0.14em]" role="group" aria-label={t.header.langSelector}>
                <button
                  data-testid="mobile-lang-es"
                  onClick={() => setLang("es")}
                  aria-pressed={lang === "es"}
                  className={`p-2 ${lang === "es" ? "text-onix-white underline underline-offset-4 decoration-onix-blue decoration-2" : "text-onix-gray"}`}
                >
                  ES
                </button>
                <span className="text-onix-gray" aria-hidden="true">/</span>
                <button
                  data-testid="mobile-lang-en"
                  onClick={() => setLang("en")}
                  aria-pressed={lang === "en"}
                  className={`p-2 ${lang === "en" ? "text-onix-white underline underline-offset-4 decoration-onix-blue decoration-2" : "text-onix-gray"}`}
                >
                  EN
                </button>
              </div>
              <ThemeToggle testid="mobile-theme-toggle" />
              <button data-testid="mobile-menu-close" className="text-onix-white p-2 -mr-2" aria-label={t.header.closeMenu} onClick={onClose}>
                <X size={22} aria-hidden="true" />
              </button>
            </div>
          </div>
          <nav className="onix-shell py-10" aria-label={t.header.mainNav}>
            <ul className="space-y-1">
              {links.map((item, i) => (
                <motion.li
                  key={item.to}
                  initial={reduce ? false : { opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
                >
                  <Link
                    data-testid={`mobile-nav-${item.to.replace(/\//g, "") || "home"}`}
                    to={item.to}
                    onClick={onClose}
                    className="flex items-center justify-between border-b border-onix-line py-5 font-serif font-bold text-2xl text-onix-white"
                  >
                    {item.label}
                    <ArrowRight size={18} className="text-onix-gray" aria-hidden="true" />
                  </Link>
                </motion.li>
              ))}
            </ul>
            <p className="eyebrow mt-12 mb-5">{t.nav.herramientas}</p>
            <ul className="space-y-3">
              {tools.map((tool) => (
                <li key={tool.slug}>
                  <Link
                    data-testid={`mobile-tool-${tool.slug}`}
                    to={`/herramientas/${tool.slug}`}
                    onClick={onClose}
                    className="flex items-baseline gap-3 text-sm font-bold uppercase tracking-[0.1em] text-onix-silver"
                  >
                    <span className="tech-label">{tool.num}</span>
                    {tool.name}
                  </Link>
                </li>
              ))}
            </ul>
            <Link data-testid="mobile-cta" to="/contacto" onClick={onClose} className="btn-onix-primary w-full mt-12">
              {t.header.cta}
            </Link>
            <p className="tech-label text-center mt-10">INTELLIGENCE DRIVEN · MISSION FOCUSED</p>
          </nav>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
